import { Head, Link } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"
import Markdown from "react-markdown"
import type { PublicVideo } from "@/types"
import { OptimizedImage } from "@/components/ui/optimized-image"
import ImageLightbox from "@/components/dashboard/image-lightbox"

type VideoMeta = {
  title: string
  description: string
  canonical: string
  image?: string
  videoUrl?: string
  durationMs?: number
  uploadDate?: string
}

const ASPECT_CLASSES: Record<PublicVideo["aspect_ratio"], string> = {
  portrait: "aspect-[9/16] max-w-sm",
  landscape: "aspect-video w-full",
  square: "aspect-square max-w-lg",
}

export default function PublicVideoShow({
  video,
  meta,
}: {
  video: PublicVideo
  meta: VideoMeta
}) {
  const propertyMeta = [
    video.bedrooms != null && video.bedrooms > 0 && `${video.bedrooms} bed`,
    video.bathrooms != null && video.bathrooms > 0 && `${video.bathrooms} bath`,
    video.property_type && capitalise(video.property_type.replace(/_/g, " ")),
  ].filter(Boolean)

  return (
    <>
      <Head title={meta.title} />

      <div className="min-h-screen bg-cream">
        {/* Minimal header */}
        <header className="border-b border-stone/20 bg-white/60 backdrop-blur-sm">
          <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3">
            <Link
              href="/"
              className="flex items-baseline gap-0 font-display text-xl font-semibold tracking-tight"
            >
              <span className="text-charcoal">Quite</span>
              <span className="text-red">Right.</span>
            </Link>
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-5 py-8 sm:py-12">
          {/* Video player */}
          {video.video_url && (
            <div className="mb-8">
              <div
                className={`${ASPECT_CLASSES[video.aspect_ratio]} mx-auto overflow-hidden rounded-2xl bg-charcoal shadow-2xl shadow-charcoal/15`}
              >
                <video
                  src={video.video_url}
                  poster={video.thumbnail_url}
                  controls
                  playsInline
                  preload="metadata"
                  className="size-full object-contain"
                />
              </div>

              {/* Social sharing icons */}
              <SocialShareButtons videoUrl={video.video_url} pageUrl={meta.canonical} address={video.address} />
            </div>
          )}

          {/* Property headline */}
          <div className="mb-8 text-center">
            <h1 className="font-display text-2xl font-semibold text-charcoal sm:text-3xl">
              {video.address}
            </h1>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
              {video.price && (
                <span className="text-lg font-semibold text-charcoal">
                  {video.price}
                </span>
              )}
              {propertyMeta.length > 0 && (
                <span className="text-sm text-warm-grey">
                  {propertyMeta.join(" · ")}
                </span>
              )}
            </div>
          </div>

          {/* Agent branding card */}
          {video.agency_name && (
            <AgentCard video={video} />
          )}

          {/* Photo gallery */}
          {video.images_data && video.images_data.length > 0 && (
            <PhotoGallery images={video.images_data} address={video.address} />
          )}

          {/* Description */}
          {video.description && (
            <section className="mb-8">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-warm-grey">
                About this property
              </h2>
              <div className="rounded-xl border border-stone/20 bg-white p-5 sm:p-6">
                <div className="prose prose-sm max-w-none text-slate prose-headings:font-display prose-headings:text-charcoal prose-p:leading-relaxed prose-a:text-red prose-a:no-underline hover:prose-a:underline prose-strong:text-charcoal prose-ul:my-2 prose-li:my-0">
                  <Markdown>{video.description}</Markdown>
                </div>
              </div>
            </section>
          )}

          {/* Features */}
          {video.features && video.features.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-warm-grey">
                Features
              </h2>
              <div className="rounded-xl border border-stone/20 bg-white p-5 sm:p-6">
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {video.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate">
                      <svg
                        className="mt-0.5 size-4 shrink-0 text-red"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m4.5 12.75 6 6 9-13.5"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* View original listing CTA */}
          {video.source_url && (
            <div className="mb-8 text-center">
              <a
                href={video.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-stone/25 bg-white px-6 py-3 text-sm font-medium text-charcoal shadow-sm transition-all hover:border-stone/40 hover:shadow-md"
              >
                <svg
                  className="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
                View original listing
              </a>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="rounded-2xl border border-stone/20 bg-white p-6 text-center sm:p-8">
            <p className="font-display text-lg font-semibold text-charcoal sm:text-xl">
              Create your own property video
            </p>
            <p className="mt-1 text-sm text-warm-grey">
              Turn any listing into a scroll-stopping video ad in minutes.
            </p>
            <Link
              href="/demo"
              className="mt-4 inline-block rounded-xl bg-red px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-red/20 transition-all hover:bg-deep-red hover:shadow-xl hover:shadow-red/30"
            >
              Get started
            </Link>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-stone/15 py-6 text-center text-xs text-warm-grey">
          <Link
            href="/"
            className="inline-flex items-baseline gap-0 font-display text-lg font-semibold tracking-tight"
          >
            <span className="text-charcoal">Quite</span>
            <span className="text-red">Right.</span>
          </Link>
          <p className="mt-1">Property video, made simple.</p>
          <nav className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link href="/how-it-works" className="transition-colors hover:text-charcoal">How it works</Link>
            <Link href="/examples" className="transition-colors hover:text-charcoal">Examples</Link>
            <Link href="/directory" className="transition-colors hover:text-charcoal">Agent directory</Link>
            <Link href="/contact" className="transition-colors hover:text-charcoal">Contact</Link>
          </nav>
        </footer>
      </div>
    </>
  )
}

function AgentCard({ video }: { video: PublicVideo }) {
  const accentColour = video.brand_colour || "#C41E2A"

  return (
    <section className="mb-8">
      <div
        className="overflow-hidden rounded-xl border border-stone/20 bg-white"
        style={{ borderTopColor: accentColour, borderTopWidth: 3 }}
      >
        <div className="flex flex-col items-center gap-4 p-5 sm:flex-row sm:p-6">
          {/* Logo */}
          {video.logo_url && (
            <OptimizedImage
              src={video.logo_url}
              alt={video.agency_name || "Agency logo"}
              className="h-12 w-auto object-contain"
            />
          )}

          {/* Details */}
          <div className="flex-1 text-center sm:text-left">
            <p className="text-sm font-semibold text-charcoal">
              {video.agency_name}
            </p>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 sm:justify-start">
              {video.agency_phone && (
                <a
                  href={`tel:${video.agency_phone}`}
                  className="text-sm text-warm-grey transition-colors hover:text-charcoal"
                >
                  {video.agency_phone}
                </a>
              )}
              {video.agency_website && (
                <a
                  href={video.agency_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-warm-grey transition-colors hover:text-charcoal"
                >
                  {displayDomain(video.agency_website)}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

type Platform = "instagram" | "facebook" | "tiktok" | "youtube"

const PLATFORM_CONFIG: Record<Platform, { label: string; uploadUrl: string; className: string; style?: React.CSSProperties }> = {
  instagram: {
    label: "Instagram",
    uploadUrl: "https://www.instagram.com",
    className: "flex size-11 items-center justify-center rounded-full text-white transition-all hover:scale-110 hover:brightness-110",
    style: { background: "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)" },
  },
  facebook: {
    label: "Facebook",
    uploadUrl: "https://www.facebook.com",
    className: "flex size-11 items-center justify-center rounded-full bg-[#1877F2] text-white transition-all hover:scale-110 hover:brightness-110",
  },
  tiktok: {
    label: "TikTok",
    uploadUrl: "https://www.tiktok.com/upload",
    className: "flex size-11 items-center justify-center rounded-full bg-black text-white transition-all hover:scale-110 hover:brightness-125",
  },
  youtube: {
    label: "YouTube",
    uploadUrl: "https://studio.youtube.com",
    className: "flex size-11 items-center justify-center rounded-full bg-[#FF0000] text-white transition-all hover:scale-110 hover:brightness-110",
  },
}

const PLATFORM_ICONS: Record<Platform, React.ReactNode> = {
  instagram: (
    <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  ),
  facebook: (
    <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  tiktok: (
    <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  ),
  youtube: (
    <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
}

async function fetchVideoFile(videoUrl: string, filename: string): Promise<File | null> {
  try {
    const response = await fetch(videoUrl)
    const blob = await response.blob()
    return new File([blob], filename, { type: "video/mp4" })
  } catch {
    return null
  }
}

function downloadVideo(videoUrl: string, filename: string) {
  const a = document.createElement("a")
  a.href = videoUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function canUseWebShare(): boolean {
  return typeof navigator !== "undefined" && !!navigator.share && !!navigator.canShare
}

function SocialShareButtons({ videoUrl, pageUrl, address }: { videoUrl: string; pageUrl: string; address: string }) {
  const [sharing, setSharing] = useState<Platform | null>(null)
  const filename = `${address.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}.mp4`

  const downloadAndRedirect = useCallback((platform: Platform) => {
    downloadVideo(videoUrl, filename)
    const config = PLATFORM_CONFIG[platform]
    setTimeout(() => {
      window.open(config.uploadUrl, "_blank")
    }, 500)
  }, [videoUrl, filename])

  const handleShare = useCallback(async (platform: Platform) => {
    if (sharing) return

    // Facebook: use share dialog with page URL (OG tags give a video preview)
    if (platform === "facebook") {
      const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl || window.location.href)}`
      window.open(shareUrl, "_blank", "width=600,height=400")
      return
    }

    // Try Web Share API (works great on mobile — opens native share sheet with video file)
    if (canUseWebShare()) {
      setSharing(platform)
      const file = await fetchVideoFile(videoUrl, filename)
      if (file && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: address,
            files: [file],
          })
        } catch (err) {
          // User cancelled — that's fine
          if (err instanceof Error && err.name !== "AbortError") {
            // Web Share failed, fall back to download + redirect
            downloadAndRedirect(platform)
          }
        }
        setSharing(null)
        return
      }
      setSharing(null)
    }

    // Desktop fallback: download the video, then open the platform
    downloadAndRedirect(platform)
  }, [sharing, videoUrl, pageUrl, address, filename, downloadAndRedirect])

  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <div className="flex items-center justify-center gap-4">
        {(Object.keys(PLATFORM_CONFIG) as Platform[]).map((platform) => {
          const config = PLATFORM_CONFIG[platform]
          const isLoading = sharing === platform
          return (
            <button
              key={platform}
              onClick={() => handleShare(platform)}
              disabled={!!sharing}
              title={`Share on ${config.label}`}
              className={`${config.className} ${sharing && !isLoading ? "opacity-50" : ""}`}
              style={config.style}
            >
              {isLoading ? (
                <svg className="size-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83" strokeLinecap="round" />
                </svg>
              ) : (
                PLATFORM_ICONS[platform]
              )}
            </button>
          )
        })}
      </div>
      <p className="text-xs text-warm-grey">Post this video to your socials</p>
    </div>
  )
}

function PhotoGallery({
  images,
  address,
}: {
  images: Array<{ url: string; thumb_url: string; description?: string }>
  address: string
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    checkScroll()
    el.addEventListener("scroll", checkScroll, { passive: true })
    const observer = new ResizeObserver(checkScroll)
    observer.observe(el)
    return () => {
      el.removeEventListener("scroll", checkScroll)
      observer.disconnect()
    }
  }, [checkScroll])

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.7
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" })
  }

  // Map to lightbox format (lightbox expects signed_id but we don't need it for view-only)
  const lightboxImages = images.map((img, i) => ({
    signed_id: String(i),
    url: img.url,
    description: img.description,
  }))

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-warm-grey">
        Photos
      </h2>

      <div className="group/strip relative">
        <div
          ref={scrollRef}
          className="flex h-44 gap-1.5 overflow-x-auto scroll-smooth rounded-xl scrollbar-none sm:h-52"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="group/img relative h-full shrink-0 cursor-pointer overflow-hidden rounded-lg bg-stone/20"
              style={{
                scrollSnapAlign: "start",
                width: i === 0 ? "clamp(240px, 40%, 360px)" : "clamp(140px, 22%, 220px)",
              }}
            >
              <OptimizedImage
                src={img.thumb_url}
                alt={`${address}, photo ${i + 1}`}
                className="size-full object-cover transition-transform duration-300 group-hover/img:scale-105"
              />

              {/* Description overlay */}
              {img.description && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-charcoal/85 via-charcoal/50 to-transparent px-3 pb-2.5 pt-8 transition-transform duration-300 ease-out group-hover/img:translate-y-0">
                  <p className="text-[11px] leading-snug text-white/90 drop-shadow-sm">
                    {img.description}
                  </p>
                </div>
              )}

            </button>
          ))}
        </div>

        {/* Scroll arrows */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scroll("left")}
            className="absolute left-2 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:shadow-lg"
          >
            <svg className="size-4 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scroll("right")}
            className="absolute right-2 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:shadow-lg"
          >
            <svg className="size-4 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}

        {/* Edge fades */}
        {canScrollLeft && (
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 rounded-l-xl bg-gradient-to-r from-cream/80 to-transparent" />
        )}
        {canScrollRight && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 rounded-r-xl bg-gradient-to-l from-cream/80 to-transparent" />
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          canDelete={false}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </section>
  )
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function displayDomain(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return url
  }
}
